import { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Users, Phone, RefreshCw, Mail, ChevronDown, ChevronUp, Lock, Shield, Building2, GraduationCap, Briefcase, AlertCircle, ClipboardCheck, MapPin, Download, FileSpreadsheet, Settings, ExternalLink, CheckCircle2, Table } from 'lucide-react';
import { AdminDataTables } from '../components/AdminDataTables';
import { FollowupV2Preview } from '../components/FollowupV2Preview';
import { UnsentFollowups } from '../components/UnsentFollowups';
import { CampusAmbassadorsAdmin } from '../components/CampusAmbassadorsAdmin';
import { NetworkMembersAdmin } from '../components/NetworkMembersAdmin';
import { EngagementCenter } from '../components/engagement/EngagementCenter';
import { ReferralsCenter } from '../components/referrals/ReferralsCenter';
import { REFRESH_EVENT } from '../hooks/useAutoRefresh';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Admin = () => {
  // Authentication State
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [authToken, setAuthToken] = useState(null);
  const [userRole, setUserRole] = useState(null); // 'admin', 'editor' or 'readonly'
  const canEditTables = userRole === 'admin' || userRole === 'editor';
  const [userName, setUserName] = useState('');

  // Dashboard State
  const [activeTab, setActiveTab] = useState('applications');
  const [stats, setStats] = useState(null);
  const [applications, setApplications] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [networkMembers, setNetworkMembers] = useState([]);
  const [employerInquiries, setEmployerInquiries] = useState([]);
  const [collegeInquiries, setCollegeInquiries] = useState([]);
  const [careerReadinessLeads, setCareerReadinessLeads] = useState([]);
  const [dehradunData, setDehradunData] = useState({ total: 0, registered_count: 0, waitlist_count: 0, registrations: [] });
  const [eventRegistrations, setEventRegistrations] = useState({ registrations: [], total: 0, filters: {} });
  const [eventFilters, setEventFilters] = useState({ event_id: '', main_concern: '', attendance_status: '', follow_up_status: '', community: '' });
  const [followupData, setFollowupData] = useState({ followups: [], stats: {} });
  const [followupSettings, setFollowupSettings] = useState({ enabled: true, test_mode: false, delay_minutes: 5 });
  const [expandedApp, setExpandedApp] = useState(null);
  const [expandedConsult, setExpandedConsult] = useState(null);
  const [expandedEmployer, setExpandedEmployer] = useState(null);
  const [expandedCollege, setExpandedCollege] = useState(null);
  const [expandedCareerCheck, setExpandedCareerCheck] = useState(null);
  const [expandedDehradun, setExpandedDehradun] = useState(null);
  const [expandedFollowup, setExpandedFollowup] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState('');
  
  // Google Sheets Integration State
  const [sheetsStatus, setSheetsStatus] = useState(null);
  const [sheetsSpreadsheetId, setSheetsSpreadsheetId] = useState('');
  const [sheetsLoading, setSheetsLoading] = useState(false);
  const [sheetsMessage, setSheetsMessage] = useState('');

  // Handle logout - defined first as it's used by authFetch
  const handleLogout = useCallback(async () => {
    try {
      await fetch(`${API_URL}/api/admin/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (err) {
      console.error('Logout error:', err);
    }
    
    setIsAuthenticated(false);
    setAuthToken(null);
    setUserRole(null);
    setUserName('');
    sessionStorage.removeItem('admin_token');
    sessionStorage.removeItem('admin_role');
    sessionStorage.removeItem('admin_name');
    setPin('');
    setStats(null);
    setApplications([]);
    setConsultations([]);
    setNetworkMembers([]);
    setEmployerInquiries([]);
    setCollegeInquiries([]);
    setCareerReadinessLeads([]);
    setDehradunData({ total: 0, registered_count: 0, waitlist_count: 0, registrations: [] });
  }, []);

  // Create authenticated fetch helper
  const authFetch = useCallback(async (url, options = {}) => {
    const token = authToken || sessionStorage.getItem('admin_token');
    if (!token) {
      throw new Error('Not authenticated');
    }
    
    const response = await fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${token}`,
      },
      credentials: 'include',
    });
    
    if (response.status === 401) {
      // Token expired or invalid - clear auth state
      handleLogout();
      throw new Error('Session expired. Please login again.');
    }
    
    return response;
  }, [authToken, handleLogout]);

  // Fetch data function
  const fetchData = useCallback(async () => {
    setLoading(true);
    setFetchError('');
    
    try {
      const [statsRes, appsRes, consultsRes, networkRes, employerRes, collegeRes, careerCheckRes, dehradunRes, eventRes, followupRes, followupSettingsRes] = await Promise.all([
        authFetch(`${API_URL}/api/admin/stats`),
        authFetch(`${API_URL}/api/admin/applications`),
        authFetch(`${API_URL}/api/admin/consultations`),
        authFetch(`${API_URL}/api/admin/network-pool`),
        authFetch(`${API_URL}/api/admin/employer-inquiries`),
        authFetch(`${API_URL}/api/admin/college-inquiries`),
        authFetch(`${API_URL}/api/admin/career-readiness-leads`),
        authFetch(`${API_URL}/api/admin/dehradun/registrations`),
        authFetch(`${API_URL}/api/admin/event-registrations`),
        authFetch(`${API_URL}/api/admin/followups`),
        authFetch(`${API_URL}/api/admin/followups/settings`)
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (appsRes.ok) setApplications(await appsRes.json());
      if (consultsRes.ok) setConsultations(await consultsRes.json());
      if (networkRes.ok) setNetworkMembers(await networkRes.json());
      if (employerRes.ok) setEmployerInquiries(await employerRes.json());
      if (collegeRes.ok) setCollegeInquiries(await collegeRes.json());
      if (careerCheckRes.ok) setCareerReadinessLeads(await careerCheckRes.json());
      if (dehradunRes.ok) setDehradunData(await dehradunRes.json());
      if (eventRes.ok) setEventRegistrations(await eventRes.json());
      if (followupRes.ok) setFollowupData(await followupRes.json());
      if (followupSettingsRes.ok) setFollowupSettings(await followupSettingsRes.json());
    } catch (err) {
      setFetchError(err.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  // Check existing session on mount
  useEffect(() => {
    const checkAuth = async () => {
      const savedToken = sessionStorage.getItem('admin_token');
      const savedRole = sessionStorage.getItem('admin_role');
      const savedName = sessionStorage.getItem('admin_name');
      
      if (savedToken) {
        try {
          const response = await fetch(`${API_URL}/api/admin/verify`, {
            headers: { 'Authorization': `Bearer ${savedToken}` },
            credentials: 'include',
          });
          
          if (response.ok) {
            const data = await response.json();
            setAuthToken(savedToken);
            setUserRole(data.role || savedRole || 'admin');
            setUserName(data.name || savedName || 'User');
            setIsAuthenticated(true);
          } else {
            sessionStorage.removeItem('admin_token');
            sessionStorage.removeItem('admin_role');
            sessionStorage.removeItem('admin_name');
          }
        } catch (err) {
          console.error('Auth check error:', err);
          sessionStorage.removeItem('admin_token');
          sessionStorage.removeItem('admin_role');
          sessionStorage.removeItem('admin_name');
        }
      }
      setIsCheckingAuth(false);
    };
    
    checkAuth();
  }, []);

  // Fetch data when authenticated
  useEffect(() => {
    if (isAuthenticated && authToken) {
      fetchData();
    }
  }, [isAuthenticated, authToken, fetchData]);

  const handlePinSubmit = async (e) => {
    e.preventDefault();
    setPinError('');
    setVerifying(true);

    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin }),
        credentials: 'include',
      });

      const data = await response.json();
      
      if (response.ok && data.token) {
        // Decode JWT to get role (simple base64 decode of payload)
        try {
          const payload = JSON.parse(atob(data.token.split('.')[1]));
          setUserRole(payload.role || 'admin');
          setUserName(payload.name || 'User');
          sessionStorage.setItem('admin_role', payload.role || 'admin');
          sessionStorage.setItem('admin_name', payload.name || 'User');
        } catch (e) {
          setUserRole('admin');
          setUserName('Admin');
        }
        
        setAuthToken(data.token);
        sessionStorage.setItem('admin_token', data.token);
        setIsAuthenticated(true);
      } else {
        setPinError(data.detail || 'Invalid PIN. Please try again.');
        setPin('');
      }
    } catch (err) {
      setPinError('Connection error. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Loading state while checking auth
  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-white text-lg">Checking authentication...</div>
      </div>
    );
  }

  // PIN Entry Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-3xl shadow-2xl p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-amber-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Shield size={32} className="text-white" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">Admin Access</h1>
              <p className="text-slate-500 text-sm">Enter PIN to access the dashboard</p>
            </div>

            <form onSubmit={handlePinSubmit} className="space-y-6">
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                <Input
                  type="password"
                  placeholder="Enter PIN"
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  className="pl-12 h-14 text-lg rounded-xl border-slate-200 focus:border-amber-500 focus:ring-amber-500"
                  data-testid="admin-pin-input"
                  autoFocus
                />
              </div>

              {pinError && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-red-600 text-sm text-center flex items-center justify-center gap-2">
                  <AlertCircle size={16} />
                  {pinError}
                </div>
              )}

              <Button
                type="submit"
                disabled={!pin || verifying}
                className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white h-14 text-lg rounded-xl font-semibold"
                data-testid="admin-pin-submit"
              >
                {verifying ? 'Verifying...' : 'Access Dashboard'}
              </Button>
            </form>

            <p className="text-center text-slate-400 text-xs mt-6">
              Elevate Careers Admin Portal • Secured Access
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Dashboard
  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-gradient-to-r from-slate-900 to-slate-800 text-white py-4 px-6 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center">
              <span className="font-bold text-white">EC</span>
            </div>
            <div>
              <h1 className="font-outfit font-bold text-lg">Admin Dashboard</h1>
              <p className="text-slate-400 text-xs">
                {userName && <span className="text-amber-400">{userName}</span>}
                {userName && ' • '}
                {userRole === 'readonly' ? 'View Only' : userRole === 'editor' ? 'Team Editor' : 'Full Access'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => { fetchData(); window.dispatchEvent(new Event(REFRESH_EVENT)); }}
              variant="outline"
              disabled={loading}
              data-testid="admin-refresh-btn"
              className="border-slate-600 text-slate-300 hover:bg-slate-700 rounded-lg"
            >
              <RefreshCw size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-red-500/50 text-red-400 hover:bg-red-500/10 rounded-lg"
              data-testid="admin-logout-btn"
            >
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Error Alert */}
        {fetchError && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-center gap-3 text-red-700">
            <AlertCircle size={20} />
            <span>{fetchError}</span>
            <Button onClick={fetchData} size="sm" variant="outline" className="ml-auto">
              Retry
            </Button>
          </div>
        )}

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-8">
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Users size={20} className="text-blue-600" />
                </div>
              </div>
              <p className="text-2xl font-bold text-slate-900">{stats.total_applications}</p>
              <span className="text-slate-500 text-xs">Applications</span>
            </div>
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                  <Users size={20} className="text-green-600" />
                </div>
              </div>
              <p className="text-2xl font-bold text-green-600">{stats.new_applications}</p>
              <span className="text-slate-500 text-xs">New Apps</span>
            </div>
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                  <Phone size={20} className="text-amber-600" />
                </div>
              </div>
              <p className="text-2xl font-bold text-slate-900">{stats.total_consultations}</p>
              <span className="text-slate-500 text-xs">Consultations</span>
            </div>
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                  <Phone size={20} className="text-orange-600" />
                </div>
              </div>
              <p className="text-2xl font-bold text-orange-600">{stats.pending_consultations}</p>
              <span className="text-slate-500 text-xs">Pending</span>
            </div>
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center">
                  <GraduationCap size={20} className="text-teal-700" />
                </div>
              </div>
              <p className="text-2xl font-bold text-teal-700">{stats.total_network_members}</p>
              <span className="text-slate-500 text-xs">Network</span>
            </div>
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Briefcase size={20} className="text-blue-600" />
                </div>
              </div>
              <p className="text-2xl font-bold text-blue-600">{stats.total_employer_inquiries}</p>
              <span className="text-slate-500 text-xs">Employers</span>
            </div>
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center">
                  <Building2 size={20} className="text-teal-600" />
                </div>
              </div>
              <p className="text-2xl font-bold text-teal-600">{stats.total_college_inquiries}</p>
              <span className="text-slate-500 text-xs">Colleges</span>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          <button
            onClick={() => setActiveTab('engagement')}
            data-testid="admin-tab-engagement"
            className={`px-5 py-2.5 rounded-xl font-medium transition-all flex items-center gap-2 ${
              activeTab === 'engagement' ? 'bg-[#0b1f4d] text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            <Phone size={16} />
            Student Engagement
          </button>
          <button
            onClick={() => setActiveTab('referrals')}
            data-testid="admin-tab-referrals"
            className={`px-5 py-2.5 rounded-xl font-medium transition-all flex items-center gap-2 ${
              activeTab === 'referrals' ? 'bg-[#0b1f4d] text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            <Users size={16} />
            Referrals
          </button>
          <button
            onClick={() => setActiveTab('applications')}
            className={`px-5 py-2.5 rounded-xl font-medium transition-all flex items-center gap-2 ${
              activeTab === 'applications'
                ? 'bg-slate-900 text-white'
                : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            <Users size={16} />
            Applications ({applications.length})
          </button>
          <button
            onClick={() => setActiveTab('consultations')}
            className={`px-5 py-2.5 rounded-xl font-medium transition-all flex items-center gap-2 ${
              activeTab === 'consultations'
                ? 'bg-slate-900 text-white'
                : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            <Phone size={16} />
            Consultations ({consultations.length})
          </button>
          <button
            onClick={() => setActiveTab('network')}
            className={`px-5 py-2.5 rounded-xl font-medium transition-all flex items-center gap-2 ${
              activeTab === 'network'
                ? 'bg-teal-700 text-white'
                : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            <GraduationCap size={16} />
            Network ({networkMembers.length})
          </button>
          <button
            onClick={() => setActiveTab('employers')}
            className={`px-5 py-2.5 rounded-xl font-medium transition-all flex items-center gap-2 ${
              activeTab === 'employers'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            <Briefcase size={16} />
            Employers ({employerInquiries.length})
          </button>
          <button
            onClick={() => setActiveTab('colleges')}
            className={`px-5 py-2.5 rounded-xl font-medium transition-all flex items-center gap-2 ${
              activeTab === 'colleges'
                ? 'bg-teal-600 text-white'
                : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            <Building2 size={16} />
            Colleges ({collegeInquiries.length})
          </button>
          <button
            onClick={() => setActiveTab('careercheck')}
            className={`px-5 py-2.5 rounded-xl font-medium transition-all flex items-center gap-2 ${
              activeTab === 'careercheck'
                ? 'bg-teal-600 text-white'
                : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            <ClipboardCheck size={16} />
            Readiness Leads ({careerReadinessLeads.length})
          </button>
          <button
            onClick={() => setActiveTab('dehradun')}
            className={`px-5 py-2.5 rounded-xl font-medium transition-all flex items-center gap-2 ${
              activeTab === 'dehradun'
                ? 'bg-amber-500 text-white'
                : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            <MapPin size={16} />
            Dehradun ({dehradunData.total})
          </button>
          <button
            onClick={() => setActiveTab('events')}
            className={`px-5 py-2.5 rounded-xl font-medium transition-all flex items-center gap-2 ${
              activeTab === 'events'
                ? 'bg-teal-600 text-white'
                : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            <Users size={16} />
            Sessions ({eventRegistrations.total || 0})
          </button>
          <button
            onClick={() => setActiveTab('followups')}
            className={`px-5 py-2.5 rounded-xl font-medium transition-all flex items-center gap-2 ${
              activeTab === 'followups'
                ? 'bg-pink-600 text-white'
                : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            <Mail size={16} />
            Follow-ups ({followupData.stats?.total || 0})
          </button>
          <button
            data-testid="admin-tab-campus-ambassadors"
            onClick={() => setActiveTab('ambassadors')}
            className={`px-5 py-2.5 rounded-xl font-medium transition-all flex items-center gap-2 ${
              activeTab === 'ambassadors'
                ? 'bg-teal-600 text-white'
                : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            <Briefcase size={16} />
            Careers Applications
          </button>
          <button
            onClick={() => setActiveTab('data')}
            className={`px-5 py-2.5 rounded-xl font-medium transition-all flex items-center gap-2 ${
              activeTab === 'data'
                ? 'bg-teal-600 text-white'
                : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            <Table size={16} />
            Data Tables
          </button>
          {/* Settings tab - only visible to admin users */}
          {userRole === 'admin' && (
            <button
              onClick={() => setActiveTab('settings')}
              className={`px-5 py-2.5 rounded-xl font-medium transition-all flex items-center gap-2 ${
                activeTab === 'settings'
                  ? 'bg-slate-700 text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-50'
              }`}
            >
              <Settings size={16} />
              Settings
            </button>
          )}
        </div>

        {/* Data Tables Tab */}
        {activeTab === 'data' && (
          <AdminDataTables
            careerReadinessLeads={careerReadinessLeads}
            applications={applications}
            consultations={consultations}
            networkMembers={networkMembers}
            collegeInquiries={collegeInquiries}
            employerInquiries={employerInquiries}
          />
        )}

        {/* Applications Tab */}
        {activeTab === 'applications' && (
          <div className="space-y-4">
            {applications.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center">
                <Users size={48} className="text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No applications yet</p>
              </div>
            ) : (
              applications.map((app, idx) => (
                <div key={app.id || idx} className="bg-white rounded-2xl shadow-sm overflow-hidden">
                  <div
                    className="p-5 cursor-pointer hover:bg-slate-50 transition-colors"
                    onClick={() => setExpandedApp(expandedApp === idx ? null : idx)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center text-white font-bold">
                          {app.full_name?.charAt(0)?.toUpperCase() || 'A'}
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900">{app.full_name}</h3>
                          <div className="flex items-center gap-3 text-sm text-slate-500">
                            <span className="flex items-center gap-1">
                              <Mail size={14} />
                              {app.email}
                            </span>
                            <span className="flex items-center gap-1 hidden sm:flex">
                              <Phone size={14} />
                              {app.phone}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          app.status === 'new' ? 'bg-green-100 text-green-700' :
                          app.status === 'contacted' ? 'bg-blue-100 text-blue-700' :
                          app.status === 'enrolled' ? 'bg-amber-100 text-amber-700' :
                          'bg-slate-100 text-slate-700'
                        }`}>
                          {app.status || 'new'}
                        </span>
                        <span className="text-slate-400 text-sm hidden sm:block">{formatDate(app.submitted_at)}</span>
                        {expandedApp === idx ? <ChevronUp size={20} className="text-slate-400" /> : <ChevronDown size={20} className="text-slate-400" />}
                      </div>
                    </div>
                  </div>

                  {expandedApp === idx && (
                    <div className="px-5 pb-5 pt-2 border-t border-slate-100">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <h4 className="font-medium text-slate-900 mb-3">Personal Details</h4>
                          <div className="space-y-2 text-sm">
                            <p><span className="text-slate-500">Phone:</span> <span className="text-slate-900">{app.phone}</span></p>
                            <p><span className="text-slate-500">City:</span> <span className="text-slate-900">{app.city || 'Not provided'}</span></p>
                            <p><span className="text-slate-500">Status:</span> <span className="text-slate-900">{app.current_status}</span></p>
                            <p><span className="text-slate-500">Degree:</span> <span className="text-slate-900">{app.degree_stream}</span></p>
                            <p><span className="text-slate-500">College:</span> <span className="text-slate-900">{app.college_name || 'Not provided'}</span></p>
                          </div>
                        </div>
                        <div>
                          <h4 className="font-medium text-slate-900 mb-3">Career Goals</h4>
                          <div className="space-y-2 text-sm">
                            <p><span className="text-slate-500">Career Clarity:</span> <span className="text-slate-900">{app.career_clarity}</span></p>
                            <p><span className="text-slate-500">Target Role:</span> <span className="text-slate-900">{app.target_role || 'Not specified'}</span></p>
                            <p><span className="text-slate-500">Interested Plan:</span> <span className="text-slate-900 font-medium">{app.interested_plan}</span></p>
                          </div>
                        </div>
                        <div className="md:col-span-2">
                          <h4 className="font-medium text-slate-900 mb-3">Responses</h4>
                          <div className="space-y-3 text-sm">
                            <div className="bg-slate-50 p-3 rounded-xl">
                              <p className="text-slate-500 mb-1">Biggest Challenge:</p>
                              <p className="text-slate-900">{app.biggest_challenge}</p>
                            </div>
                            <div className="bg-slate-50 p-3 rounded-xl">
                              <p className="text-slate-500 mb-1">Why Join:</p>
                              <p className="text-slate-900">{app.why_join || 'Not provided'}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {/* Consultations Tab */}
        {activeTab === 'consultations' && (
          <div className="space-y-4">
            {consultations.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center">
                <Phone size={48} className="text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No consultation requests yet</p>
              </div>
            ) : (
              consultations.map((consult, idx) => (
                <div key={consult.id || idx} className="bg-white rounded-2xl shadow-sm overflow-hidden">
                  <div
                    className="p-5 cursor-pointer hover:bg-slate-50 transition-colors"
                    onClick={() => setExpandedConsult(expandedConsult === idx ? null : idx)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center text-white font-bold">
                          {consult.full_name?.charAt(0)?.toUpperCase() || 'C'}
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900">{consult.full_name}</h3>
                          <div className="flex items-center gap-3 text-sm text-slate-500">
                            <span className="flex items-center gap-1">
                              <Mail size={14} />
                              {consult.email}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          consult.status === 'pending' ? 'bg-orange-100 text-orange-700' :
                          consult.status === 'completed' ? 'bg-green-100 text-green-700' :
                          'bg-slate-100 text-slate-700'
                        }`}>
                          {consult.status || 'pending'}
                        </span>
                        <span className="text-slate-400 text-sm hidden sm:block">{formatDate(consult.submitted_at)}</span>
                        {expandedConsult === idx ? <ChevronUp size={20} className="text-slate-400" /> : <ChevronDown size={20} className="text-slate-400" />}
                      </div>
                    </div>
                  </div>

                  {expandedConsult === idx && (
                    <div className="px-5 pb-5 pt-2 border-t border-slate-100">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <h4 className="font-medium text-slate-900 mb-3">Details</h4>
                          <div className="space-y-2 text-sm">
                            <p><span className="text-slate-500">Phone:</span> <span className="text-slate-900">{consult.phone}</span></p>
                            <p><span className="text-slate-500">Current Situation:</span> <span className="text-slate-900">{consult.current_situation}</span></p>
                            <p><span className="text-slate-500">Preferred Time:</span> <span className="text-slate-900">{consult.preferred_time || 'Flexible'}</span></p>
                          </div>
                        </div>
                        {consult.questions && (
                          <div>
                            <h4 className="font-medium text-slate-900 mb-3">Questions</h4>
                            <div className="bg-slate-50 p-3 rounded-xl text-sm text-slate-900">
                              {consult.questions}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {/* Talent Network Tab */}
        {activeTab === 'network' && (
          <NetworkMembersAdmin authFetch={authFetch} apiUrl={API_URL} canEdit={canEditTables} />
        )}

        {activeTab === 'employers' && (
          <div className="space-y-4">
            {employerInquiries.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center">
                <Briefcase size={48} className="text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No employer inquiries yet</p>
              </div>
            ) : (
              employerInquiries.map((inquiry, idx) => (
                <div key={inquiry.id || idx} className="bg-white rounded-2xl shadow-sm overflow-hidden">
                  <div
                    className="p-5 cursor-pointer hover:bg-slate-50 transition-colors"
                    onClick={() => setExpandedEmployer(expandedEmployer === idx ? null : idx)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center text-white font-bold">
                          {inquiry.company_name?.charAt(0)?.toUpperCase() || 'E'}
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900">{inquiry.company_name}</h3>
                          <p className="text-sm text-slate-500">{inquiry.contact_person} • {inquiry.designation}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                          {inquiry.experience_level}
                        </span>
                        <span className="text-slate-400 text-sm hidden sm:block">{formatDate(inquiry.submitted_at)}</span>
                        {expandedEmployer === idx ? <ChevronUp size={20} className="text-slate-400" /> : <ChevronDown size={20} className="text-slate-400" />}
                      </div>
                    </div>
                  </div>

                  {expandedEmployer === idx && (
                    <div className="px-5 pb-5 pt-2 border-t border-slate-100">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <h4 className="font-medium text-slate-900 mb-3">Contact</h4>
                          <div className="space-y-2 text-sm">
                            <p><span className="text-slate-500">Email:</span> <span className="text-slate-900">{inquiry.work_email}</span></p>
                            <p><span className="text-slate-500">Phone:</span> <span className="text-slate-900">{inquiry.phone}</span></p>
                            {inquiry.company_website && (
                              <p><span className="text-slate-500">Website:</span> <a href={inquiry.company_website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{inquiry.company_website}</a></p>
                            )}
                          </div>
                        </div>
                        <div>
                          <h4 className="font-medium text-slate-900 mb-3">Hiring Details</h4>
                          <div className="space-y-2 text-sm">
                            <p><span className="text-slate-500">Roles:</span> <span className="text-slate-900">{inquiry.roles_hiring}</span></p>
                            <p><span className="text-slate-500">Openings:</span> <span className="text-slate-900">{inquiry.num_openings || 'Not specified'}</span></p>
                            <p><span className="text-slate-500">Location:</span> <span className="text-slate-900">{inquiry.location_preference || 'Not specified'}</span></p>
                            <p><span className="text-slate-500">Timeline:</span> <span className="text-slate-900">{inquiry.hiring_timeline || 'Not specified'}</span></p>
                          </div>
                        </div>
                        {inquiry.required_skills && (
                          <div className="md:col-span-2">
                            <h4 className="font-medium text-slate-900 mb-3">Required Skills</h4>
                            <p className="text-sm text-slate-700 bg-slate-50 p-3 rounded-xl">{inquiry.required_skills}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {/* Colleges Tab */}
        {activeTab === 'colleges' && (
          <div className="space-y-4">
            {collegeInquiries.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center">
                <Building2 size={48} className="text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No college inquiries yet</p>
              </div>
            ) : (
              collegeInquiries.map((inquiry, idx) => (
                <div key={inquiry.id || idx} className="bg-white rounded-2xl shadow-sm overflow-hidden">
                  <div
                    className="p-5 cursor-pointer hover:bg-slate-50 transition-colors"
                    onClick={() => setExpandedCollege(expandedCollege === idx ? null : idx)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center text-white font-bold">
                          {inquiry.institution_name?.charAt(0)?.toUpperCase() || 'C'}
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900">{inquiry.institution_name}</h3>
                          <p className="text-sm text-slate-500">{inquiry.contact_person} • {inquiry.city}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-teal-100 text-teal-700">
                          {inquiry.num_students || 'N/A'} students
                        </span>
                        <span className="text-slate-400 text-sm hidden sm:block">{formatDate(inquiry.submitted_at)}</span>
                        {expandedCollege === idx ? <ChevronUp size={20} className="text-slate-400" /> : <ChevronDown size={20} className="text-slate-400" />}
                      </div>
                    </div>
                  </div>

                  {expandedCollege === idx && (
                    <div className="px-5 pb-5 pt-2 border-t border-slate-100">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <h4 className="font-medium text-slate-900 mb-3">Contact</h4>
                          <div className="space-y-2 text-sm">
                            <p><span className="text-slate-500">Contact Person:</span> <span className="text-slate-900">{inquiry.contact_person}</span></p>
                            <p><span className="text-slate-500">Designation:</span> <span className="text-slate-900">{inquiry.designation}</span></p>
                            <p><span className="text-slate-500">Email:</span> <span className="text-slate-900">{inquiry.official_email}</span></p>
                            <p><span className="text-slate-500">Phone:</span> <span className="text-slate-900">{inquiry.phone}</span></p>
                          </div>
                        </div>
                        <div>
                          <h4 className="font-medium text-slate-900 mb-3">Program Details</h4>
                          <div className="space-y-2 text-sm">
                            <p><span className="text-slate-500">Degrees/Depts:</span> <span className="text-slate-900">{inquiry.degrees_departments || 'Not specified'}</span></p>
                            <p><span className="text-slate-500">Grad Years:</span> <span className="text-slate-900">{inquiry.graduation_years || 'Not specified'}</span></p>
                            <p><span className="text-slate-500">Timeline:</span> <span className="text-slate-900">{inquiry.preferred_timeline || 'Not specified'}</span></p>
                          </div>
                        </div>
                        <div className="md:col-span-2">
                          <h4 className="font-medium text-slate-900 mb-3">Required Program</h4>
                          <p className="text-sm text-slate-700 bg-slate-50 p-3 rounded-xl">{inquiry.required_program}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {/* Career Readiness Leads Tab */}
        {activeTab === 'careercheck' && (
          <div className="space-y-4">
            {careerReadinessLeads.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center">
                <ClipboardCheck size={48} className="text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No career readiness check submissions yet</p>
              </div>
            ) : (
              careerReadinessLeads.map((lead, idx) => (
                <div key={lead.id || idx} className="bg-white rounded-2xl shadow-sm overflow-hidden">
                  <div 
                    className="p-5 cursor-pointer hover:bg-slate-50 transition-colors"
                    onClick={() => setExpandedCareerCheck(expandedCareerCheck === idx ? null : idx)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
                          <ClipboardCheck size={20} className="text-teal-700" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900">{lead.firstName}</h3>
                          <p className="text-slate-500 text-sm">{lead.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          lead.priorityArea === 'career_direction' ? 'bg-blue-100 text-blue-700' :
                          lead.priorityArea === 'profile_strength' ? 'bg-teal-100 text-teal-700' :
                          lead.priorityArea === 'interview_readiness' ? 'bg-amber-100 text-amber-700' :
                          'bg-green-100 text-green-700'
                        }`}>
                          {lead.priorityArea === 'career_direction' ? 'Career Direction' :
                           lead.priorityArea === 'profile_strength' ? 'Profile Strength' :
                           lead.priorityArea === 'interview_readiness' ? 'Interview Readiness' :
                           'Job Search'}
                        </span>
                        <span className="text-slate-400 text-sm hidden sm:block">{formatDate(lead.submitted_at)}</span>
                        {expandedCareerCheck === idx ? <ChevronUp size={20} className="text-slate-400" /> : <ChevronDown size={20} className="text-slate-400" />}
                      </div>
                    </div>
                  </div>

                  {expandedCareerCheck === idx && (
                    <div className="px-5 pb-5 pt-2 border-t border-slate-100">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <h4 className="font-medium text-slate-900 mb-3">Contact Details</h4>
                          <div className="space-y-2 text-sm">
                            <p><span className="text-slate-500">WhatsApp:</span> <span className="text-slate-900">{lead.whatsapp}</span></p>
                            <p><span className="text-slate-500">Stage:</span> <span className="text-slate-900">{lead.stage}</span></p>
                            <p><span className="text-slate-500">Qualification:</span> <span className="text-slate-900">{lead.qualification}</span></p>
                            <p><span className="text-slate-500">Target Role:</span> <span className="text-slate-900">{lead.targetRole || 'Not specified'}</span></p>
                          </div>
                        </div>
                        <div>
                          <h4 className="font-medium text-slate-900 mb-3">Assessment Results</h4>
                          <div className="space-y-2 text-sm">
                            {lead.bands && Object.entries(lead.bands).map(([dimension, band]) => (
                              <p key={dimension}>
                                <span className="text-slate-500">
                                  {dimension === 'career_direction' ? 'Career Direction' :
                                   dimension === 'profile_strength' ? 'Profile Strength' :
                                   dimension === 'interview_readiness' ? 'Interview Readiness' :
                                   'Job Search'}:
                                </span>{' '}
                                <span className={`font-medium ${
                                  band === 'Strong' ? 'text-teal-700' :
                                  band === 'Developing Well' ? 'text-blue-600' :
                                  band === 'Needs Attention' ? 'text-amber-600' :
                                  'text-orange-600'
                                }`}>{band}</span>
                              </p>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {/* Dehradun Registrations Tab */}
        {activeTab === 'dehradun' && (
          <div className="space-y-4">
            {/* Dehradun Stats */}
            <div className="bg-white rounded-2xl p-5 mb-4">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-6">
                  <div>
                    <p className="text-3xl font-bold text-slate-900">{dehradunData.total}</p>
                    <span className="text-slate-500 text-sm">Total Registrations</span>
                  </div>
                  <div className="h-10 w-px bg-slate-200"></div>
                  <div>
                    <p className="text-2xl font-bold text-teal-700">{dehradunData.registered_count}</p>
                    <span className="text-slate-500 text-sm">Registered</span>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-amber-600">{dehradunData.waitlist_count}</p>
                    <span className="text-slate-500 text-sm">Waitlist</span>
                  </div>
                </div>
                <Button
                  onClick={async () => {
                    try {
                      const token = sessionStorage.getItem('admin_token');
                      const response = await fetch(`${API_URL}/api/admin/dehradun/export-csv`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                      });
                      if (response.ok) {
                        const blob = await response.blob();
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = 'dehradun_registrations.csv';
                        document.body.appendChild(a);
                        a.click();
                        window.URL.revokeObjectURL(url);
                        a.remove();
                      }
                    } catch (err) {
                      console.error('Export error:', err);
                    }
                  }}
                  variant="outline"
                  className="border-slate-300 text-slate-700 hover:bg-slate-50"
                >
                  <Download size={16} className="mr-2" />
                  Export CSV
                </Button>
              </div>
            </div>

            {dehradunData.registrations.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center">
                <MapPin size={48} className="text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No Dehradun registrations yet</p>
              </div>
            ) : (
              dehradunData.registrations.map((reg, idx) => (
                <div key={reg.id || idx} className="bg-white rounded-2xl shadow-sm overflow-hidden">
                  <div
                    onClick={() => setExpandedDehradun(expandedDehradun === idx ? null : idx)}
                    className="p-5 cursor-pointer hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                          reg.status === 'REGISTERED' ? 'bg-teal-100' : 'bg-amber-100'
                        }`}>
                          <MapPin size={20} className={reg.status === 'REGISTERED' ? 'text-teal-700' : 'text-amber-600'} />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900">{reg.full_name}</h3>
                          <p className="text-slate-500 text-sm">{reg.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          reg.status === 'REGISTERED' ? 'bg-teal-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                        }`}>
                          {reg.status}
                        </span>
                        {reg.source && (
                          <span className="px-2 py-1 rounded text-xs bg-slate-100 text-slate-600">
                            {reg.source}
                          </span>
                        )}
                        <span className="text-slate-400 text-sm hidden sm:block">{formatDate(reg.registered_at)}</span>
                        {expandedDehradun === idx ? <ChevronUp size={20} className="text-slate-400" /> : <ChevronDown size={20} className="text-slate-400" />}
                      </div>
                    </div>
                  </div>

                  {expandedDehradun === idx && (
                    <div className="px-5 pb-5 pt-2 border-t border-slate-100">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <h4 className="font-medium text-slate-900 mb-3">Student Details</h4>
                          <div className="space-y-2 text-sm">
                            <p><span className="text-slate-500">Phone:</span> <span className="text-slate-900">{reg.phone}</span></p>
                            <p><span className="text-slate-500">College:</span> <span className="text-slate-900">{reg.college}</span></p>
                            <p><span className="text-slate-500">Degree:</span> <span className="text-slate-900">{reg.degree}</span></p>
                            <p><span className="text-slate-500">Graduation Year:</span> <span className="text-slate-900">{reg.graduation_year}</span></p>
                            <p><span className="text-slate-500">Current Status:</span> <span className="text-slate-900">{reg.current_status}</span></p>
                            <p><span className="text-slate-500">Consent Updates:</span> <span className="text-slate-900">{reg.consent_updates ? 'Yes' : 'No'}</span></p>
                          </div>
                        </div>
                        <div>
                          <h4 className="font-medium text-slate-900 mb-3">Career Question</h4>
                          <p className="text-slate-700 text-sm whitespace-pre-wrap bg-slate-50 p-3 rounded-lg">{reg.career_question}</p>
                          
                          {reg.tried_already && (
                            <>
                              <h4 className="font-medium text-slate-900 mt-4 mb-3">What They&apos;ve Tried</h4>
                              <p className="text-slate-700 text-sm whitespace-pre-wrap bg-slate-50 p-3 rounded-lg">{reg.tried_already}</p>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {/* Events/Sessions Tab */}
        {activeTab === 'events' && (
          <div className="space-y-6">
            {/* Filters */}
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
              <div className="flex flex-wrap gap-4 items-center">
                <select
                  value={eventFilters.event_id}
                  onChange={(e) => setEventFilters(f => ({...f, event_id: e.target.value}))}
                  className="px-3 py-2 border border-slate-200 rounded-lg text-sm font-medium"
                  data-testid="event-filter-select"
                >
                  <option value="">All Sessions</option>
                  <option value="session-oct-17-2026">What After Graduation? (date TBC)</option>
                  <option value="session-oct-13-2026">Oct 13: Online Career</option>
                  <option value="session-oct-2026">Oct 3: What to Do Next?</option>
                </select>
                <select
                  value={eventFilters.main_concern}
                  onChange={(e) => setEventFilters(f => ({...f, main_concern: e.target.value}))}
                  className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
                >
                  <option value="">All Concerns</option>
                  <option value="Unsure about my career direction">Unsure about career direction</option>
                  <option value="Finding internships or job opportunities">Finding internships/jobs</option>
                  <option value="Lack of practical experience">Lack of practical experience</option>
                  <option value="Improving my CV or LinkedIn profile">Improving CV/LinkedIn</option>
                  <option value="Preparing for interviews">Interview preparation</option>
                  <option value="Other">Other</option>
                </select>
                <select
                  value={eventFilters.attendance_status}
                  onChange={(e) => setEventFilters(f => ({...f, attendance_status: e.target.value}))}
                  className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
                >
                  <option value="">All Attendance</option>
                  <option value="registered">Registered</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="attended">Attended</option>
                  <option value="no-show">No Show</option>
                </select>
                <select
                  value={eventFilters.follow_up_status}
                  onChange={(e) => setEventFilters(f => ({...f, follow_up_status: e.target.value}))}
                  className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
                >
                  <option value="">All Follow-up</option>
                  <option value="pending">Pending</option>
                  <option value="contacted">Contacted</option>
                  <option value="converted">Converted</option>
                  <option value="not-interested">Not Interested</option>
                </select>
                <select
                  value={eventFilters.community}
                  onChange={(e) => setEventFilters(f => ({...f, community: e.target.value}))}
                  className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  data-testid="event-community-filter"
                >
                  <option value="">All Community</option>
                  <option value="yes">Community opt-in</option>
                  <option value="no">No opt-in</option>
                </select>
                <Button
                  onClick={() => {
                    const data = eventRegistrations.registrations || [];
                    const filtered = data.filter(r => {
                      if (eventFilters.event_id && r.event_id !== eventFilters.event_id) return false;
                      if (eventFilters.main_concern && r.main_concern !== eventFilters.main_concern) return false;
                      if (eventFilters.attendance_status && r.attendance_status !== eventFilters.attendance_status) return false;
                      if (eventFilters.follow_up_status && r.follow_up_status !== eventFilters.follow_up_status) return false;
                  if (eventFilters.community && Boolean(r.community_opt_in) !== (eventFilters.community === 'yes')) return false;
                      return true;
                    });
                    const headers = ['Name', 'Email', 'WhatsApp', 'Institution', 'Status', 'Education Mode', 'Concern', 'Question', 'University Support', 'Career Lab', 'WhatsApp Updates', 'Community Opt-in', 'Community Opt-in At', 'Registered', 'Attendance', 'Follow-up', 'Event'];
                    const rows = filtered.map(r => [
                      r.full_name, r.email, r.whatsapp, r.institution || '', r.current_status,
                      r.education_mode || '', r.main_concern || r.main_challenge || '', r.question || '', 
                      r.university_support || '', r.career_lab_optin ? 'Yes' : 'No',
                      r.whatsapp_consent ? 'Yes' : 'No', r.community_opt_in ? 'Yes' : 'No', r.community_opt_in_at || '',
                      r.registered_at?.split('T')[0] || '', r.attendance_status, r.follow_up_status, r.event_id || ''
                    ]);
                    const csv = [headers.join(','), ...rows.map(r => r.map(c => { const v = String(c ?? ''); return `"${(/^[=+\-@\t\r]/.test(v) ? `'${v}` : v).replace(/"/g, '""')}"`; }).join(','))].join('\n');
                    const blob = new Blob([csv], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `session-registrations-${eventFilters.event_id || 'all'}-${new Date().toISOString().split('T')[0]}.csv`;
                    a.click();
                  }}
                  variant="outline"
                  className="ml-auto"
                  data-testid="export-sessions-csv"
                >
                  <Download size={16} className="mr-2" />
                  Export CSV
                </Button>
              </div>
            </div>

            {/* Registrations List */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
              <div className="p-4 border-b border-slate-100">
                <h3 className="font-semibold text-slate-800">
                  Session Registrations ({(eventRegistrations.registrations || []).filter(r => {
                    if (eventFilters.event_id && r.event_id !== eventFilters.event_id) return false;
                    if (eventFilters.main_concern && r.main_concern !== eventFilters.main_concern) return false;
                    if (eventFilters.attendance_status && r.attendance_status !== eventFilters.attendance_status) return false;
                    if (eventFilters.follow_up_status && r.follow_up_status !== eventFilters.follow_up_status) return false;
                  if (eventFilters.community && Boolean(r.community_opt_in) !== (eventFilters.community === 'yes')) return false;
                    return true;
                  }).length})
                </h3>
                <p className="text-sm text-slate-500">
                  {eventFilters.event_id === 'session-oct-17-2026'
                    ? 'What After Graduation? – Date TBC'
                    : eventFilters.event_id === 'session-oct-13-2026' 
                    ? 'Studying Online? What About Your Career? – Oct 13, 2026' 
                    : eventFilters.event_id === 'session-oct-2026'
                    ? 'Not Sure What to Do Next? – Oct 3, 2026'
                    : 'All Sessions'}
                </p>
              </div>
              <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto">
                {(eventRegistrations.registrations || []).filter(r => {
                  if (eventFilters.event_id && r.event_id !== eventFilters.event_id) return false;
                  if (eventFilters.main_concern && r.main_concern !== eventFilters.main_concern) return false;
                  if (eventFilters.attendance_status && r.attendance_status !== eventFilters.attendance_status) return false;
                  if (eventFilters.follow_up_status && r.follow_up_status !== eventFilters.follow_up_status) return false;
                  if (eventFilters.community && Boolean(r.community_opt_in) !== (eventFilters.community === 'yes')) return false;
                  return true;
                }).map((reg, index) => (
                  <div key={reg.id || index} className="p-4 hover:bg-slate-50" data-testid={`session-reg-${index}`}>
                    <div className="flex justify-between items-start gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="font-medium text-slate-800">{reg.full_name}</span>
                          <span className={`px-2 py-0.5 text-xs rounded-full ${
                            reg.attendance_status === 'attended' ? 'bg-green-100 text-green-700' :
                            reg.attendance_status === 'confirmed' ? 'bg-blue-100 text-blue-700' :
                            reg.attendance_status === 'no-show' ? 'bg-red-100 text-red-700' :
                            'bg-slate-100 text-slate-600'
                          }`}>
                            {reg.attendance_status}
                          </span>
                          <span className={`px-2 py-0.5 text-xs rounded-full ${
                            reg.follow_up_status === 'converted' ? 'bg-teal-100 text-teal-700' :
                            reg.follow_up_status === 'contacted' ? 'bg-cyan-100 text-cyan-700' :
                            reg.follow_up_status === 'not-interested' ? 'bg-gray-100 text-gray-600' :
                            'bg-amber-100 text-amber-700'
                          }`}>
                            {reg.follow_up_status}
                          </span>
                          {reg.community_opt_in && (
                            <span data-testid={`session-reg-community-${index}`} className="px-2 py-0.5 text-xs rounded-full bg-teal-100 text-teal-700">
                              Community opt-in
                            </span>
                          )}
                          {reg.career_lab_optin && (
                            <span className="px-2 py-0.5 text-xs rounded-full bg-teal-100 text-teal-700">
                              Career Lab
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-slate-600 space-y-0.5">
                          <p><Mail size={12} className="inline mr-1" />{reg.email}</p>
                          <p><Phone size={12} className="inline mr-1" />{reg.whatsapp}</p>
                          {(reg.institution || reg.organization) && <p><Building2 size={12} className="inline mr-1" />{reg.institution || reg.organization}</p>}
                        </div>
                        <div className="mt-2 flex flex-wrap gap-2 text-xs">
                          <span className="px-2 py-1 bg-slate-100 rounded">{reg.current_status}</span>
                          {reg.education_mode && (
                            <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded">{reg.education_mode}</span>
                          )}
                          <span className="px-2 py-1 bg-teal-50 text-teal-700 rounded">{reg.main_concern || reg.main_challenge}</span>
                        </div>
                        {reg.question && (
                          <div className="mt-2 p-2 bg-amber-50 rounded-lg text-xs text-amber-800">
                            <strong>Q:</strong> {reg.question}
                          </div>
                        )}
                        {reg.university_support && (
                          <div className="mt-1 text-xs text-slate-500">
                            <strong>University support:</strong> {reg.university_support}
                          </div>
                        )}
                      </div>
                      <div className="text-right text-xs text-slate-500 space-y-1">
                        <div>{reg.registered_at?.split('T')[0]}</div>
                        <div className="text-[10px] text-slate-400">
                          {reg.event_id === 'session-oct-17-2026' ? 'Oct 17' : reg.event_id === 'session-oct-13-2026' ? 'Oct 13' : reg.event_id === 'session-oct-2026' ? 'Oct 3' : reg.event_id}
                        </div>
                      </div>
                    </div>
                    {canEditTables && (
                      <div className="mt-3 pt-3 border-t border-slate-100 flex gap-2">
                        <select
                          value={reg.attendance_status}
                          onChange={async (e) => {
                            try {
                              await authFetch(`${API_URL}/api/admin/event-registrations/${reg.id}`, {
                                method: 'PUT',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ attendance_status: e.target.value })
                              });
                              fetchData();
                            } catch (err) { console.error(err); }
                          }}
                          className="text-xs px-2 py-1 border rounded"
                        >
                          <option value="registered">Registered</option>
                          <option value="confirmed">Confirmed</option>
                          <option value="attended">Attended</option>
                          <option value="no-show">No Show</option>
                        </select>
                        <select
                          value={reg.follow_up_status}
                          onChange={async (e) => {
                            try {
                              await authFetch(`${API_URL}/api/admin/event-registrations/${reg.id}`, {
                                method: 'PUT',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ follow_up_status: e.target.value })
                              });
                              fetchData();
                            } catch (err) { console.error(err); }
                          }}
                          className="text-xs px-2 py-1 border rounded"
                        >
                          <option value="pending">Pending</option>
                          <option value="contacted">Contacted</option>
                          <option value="converted">Converted</option>
                          <option value="not-interested">Not Interested</option>
                        </select>
                      </div>
                    )}
                  </div>
                ))}
                {(eventRegistrations.registrations || []).length === 0 && (
                  <div className="p-8 text-center text-slate-500">
                    No registrations yet
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Follow-ups Tab - AI-Powered Consultation Offers */}
        {activeTab === 'ambassadors' && (
          <CampusAmbassadorsAdmin authFetch={authFetch} apiUrl={API_URL} canEdit={canEditTables} />
        )}

        {activeTab === 'referrals' && (
          <ReferralsCenter authFetch={authFetch} apiUrl={API_URL} canEdit={userRole === 'admin'} />
        )}

        {activeTab === 'engagement' && (
          <EngagementCenter authFetch={authFetch} apiUrl={API_URL} canSend={canEditTables} canEditTemplates={userRole === 'admin'} />
        )}

        {activeTab === 'followups' && (
          <div className="space-y-6">
            {/* Settings Card */}
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-pink-100 rounded-xl">
                    <Mail className="w-5 h-5 text-pink-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">AI Follow-up Automation</h3>
                    <p className="text-xs text-slate-500">Personalized consultation offers sent after assessment</p>
                  </div>
                </div>
                {userRole === 'admin' && (
                  <div className="flex items-center gap-4 flex-wrap">
                    <FollowupV2Preview authFetch={authFetch} apiUrl={API_URL} />
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      Template
                      <select
                        data-testid="followup-template-version-select"
                        value={followupSettings.template_version || 'v1'}
                        onChange={async (e) => {
                          const v = e.target.value;
                          if (v === 'v2' && !window.confirm('Switch live follow-up emails to the revised v2 template?')) return;
                          try {
                            await authFetch(`${API_URL}/api/admin/followups/settings`, {
                              method: 'PUT',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ template_version: v })
                            });
                            setFollowupSettings(prev => ({ ...prev, template_version: v }));
                          } catch (err) { console.error(err); }
                        }}
                        className="border border-slate-200 rounded-lg px-2 py-1 text-sm"
                      >
                        <option value="v1">v1 (current)</option>
                        <option value="v2">v2 (revised)</option>
                      </select>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={followupSettings.test_mode}
                        data-testid="followup-test-mode-toggle"
                        onChange={async (e) => {
                          const value = e.target.checked;
                          try {
                            const res = await authFetch(`${API_URL}/api/admin/followups/settings`, {
                              method: 'PUT',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ test_mode: value })
                            });
                            if (!res.ok) throw new Error('Failed to save setting');
                            const { settings } = await res.json();
                            setFollowupSettings(prev => ({ ...prev, ...settings }));
                          } catch (err) { console.error(err); alert('Could not update the setting. Please try again.'); }
                        }}
                        className="w-4 h-4 text-amber-600 rounded"
                      />
                      <span className="text-sm text-amber-700 font-medium">Test Mode</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={followupSettings.enabled}
                        data-testid="followup-enabled-toggle"
                        onChange={async (e) => {
                          const value = e.target.checked;
                          try {
                            const res = await authFetch(`${API_URL}/api/admin/followups/settings`, {
                              method: 'PUT',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ enabled: value })
                            });
                            if (!res.ok) throw new Error('Failed to save setting');
                            const { settings } = await res.json();
                            setFollowupSettings(prev => ({ ...prev, ...settings }));
                          } catch (err) { console.error(err); alert('Could not update the setting. Please try again.'); }
                        }}
                        className="w-4 h-4 text-emerald-600 rounded"
                      />
                      <span className={`text-sm font-medium ${followupSettings.enabled ? 'text-emerald-700' : 'text-slate-500'}`}>
                        {followupSettings.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </label>
                  </div>
                )}
              </div>
              
              {/* Stats */}
              <div className="grid grid-cols-4 gap-4">
                <div className="p-3 bg-slate-50 rounded-xl text-center">
                  <p className="text-2xl font-bold text-slate-900">{followupData.stats?.total || 0}</p>
                  <p className="text-xs text-slate-500">Total Sent</p>
                </div>
                <div className="p-3 bg-emerald-50 rounded-xl text-center">
                  <p className="text-2xl font-bold text-emerald-700">{followupData.stats?.booked || 0}</p>
                  <p className="text-xs text-emerald-600">Booked</p>
                </div>
                <div className="p-3 bg-amber-50 rounded-xl text-center">
                  <p className="text-2xl font-bold text-amber-700">{followupData.stats?.pending_followup || 0}</p>
                  <p className="text-xs text-amber-600">Pending Follow-up</p>
                </div>
                <div className="p-3 bg-blue-50 rounded-xl text-center">
                  <p className="text-2xl font-bold text-blue-700">
                    {followupData.stats?.total > 0 
                      ? Math.round((followupData.stats?.booked / followupData.stats?.total) * 100) 
                      : 0}%
                  </p>
                  <p className="text-xs text-blue-600">Conversion</p>
                </div>
              </div>
            </div>

            <UnsentFollowups authFetch={authFetch} apiUrl={API_URL} canSend={userRole === 'admin'} />

            {/* Follow-ups List */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
              <div className="p-4 border-b border-slate-100">
                <h3 className="font-semibold text-slate-800">Follow-up Records</h3>
              </div>
              <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto">
                {(followupData.followups || []).map((fu, index) => (
                  <div key={fu.id || index} className="p-4 hover:bg-slate-50">
                    <div className="flex justify-between items-start gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="font-medium text-slate-800">{fu.student_name}</span>
                          <span className={`px-2 py-0.5 text-xs rounded-full ${
                            fu.status === 'booked' ? 'bg-emerald-100 text-emerald-700' :
                            fu.status === 'offer_sent' ? 'bg-blue-100 text-blue-700' :
                            fu.status === 'contacted' ? 'bg-teal-100 text-teal-700' :
                            fu.status === 'opted_out' ? 'bg-gray-100 text-gray-600' :
                            fu.status === 'not_interested' ? 'bg-red-100 text-red-600' :
                            'bg-amber-100 text-amber-700'
                          }`}>
                            {fu.status?.replace(/_/g, ' ')}
                          </span>
                          {fu.consultation_booked && (
                            <span className="px-2 py-0.5 text-xs rounded-full bg-emerald-100 text-emerald-700">
                              ✓ Booked
                            </span>
                          )}
                          {fu.test_mode && (
                            <span className="px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700">
                              Test
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-slate-600 space-y-0.5">
                          <p><Mail size={12} className="inline mr-1" />{fu.student_email}</p>
                          {fu.stage && <p className="text-xs text-slate-500">{fu.stage} • {fu.qualification}</p>}
                        </div>
                        <div className="mt-2 flex flex-wrap gap-2 text-xs">
                          <span className="px-2 py-1 bg-pink-50 text-pink-700 rounded">
                            Gap: {fu.primary_gap || 'N/A'}
                          </span>
                          {fu.generated_by && (
                            <span className="px-2 py-1 bg-slate-100 text-slate-600 rounded">
                              {fu.generated_by === 'ai' ? '🤖 AI Generated' : '📝 Template'}
                            </span>
                          )}
                        </div>
                        {expandedFollowup === fu.id && (
                          <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                            <p className="text-xs font-medium text-slate-500 mb-1">Subject:</p>
                            <p className="text-sm text-slate-700 mb-2">{fu.email_subject}</p>
                            <p className="text-xs font-medium text-slate-500 mb-1">Email Preview:</p>
                            <p className="text-xs text-slate-600 whitespace-pre-wrap">{fu.email_body_text?.substring(0, 500)}...</p>
                          </div>
                        )}
                      </div>
                      <div className="text-right text-xs text-slate-500 space-y-1">
                        <div>{fu.offer_sent_at?.split('T')[0] || fu.created_at?.split('T')[0]}</div>
                        <button
                          onClick={() => setExpandedFollowup(expandedFollowup === fu.id ? null : fu.id)}
                          className="text-teal-600 hover:text-[#0b1f4d]"
                        >
                          {expandedFollowup === fu.id ? 'Hide' : 'Preview'}
                        </button>
                      </div>
                    </div>
                    {canEditTables && (
                      <div className="mt-3 pt-3 border-t border-slate-100 flex gap-2 flex-wrap">
                        <select
                          value={fu.status}
                          onChange={async (e) => {
                            try {
                              await authFetch(`${API_URL}/api/admin/followups/${fu.id}`, {
                                method: 'PUT',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ status: e.target.value })
                              });
                              fetchData();
                            } catch (err) { console.error(err); }
                          }}
                          className="text-xs px-2 py-1 border rounded"
                        >
                          <option value="offer_sent">Offer Sent</option>
                          <option value="follow_up_due">Follow-up Due</option>
                          <option value="contacted">Contacted</option>
                          <option value="booked">Booked</option>
                          <option value="not_interested">Not Interested</option>
                          <option value="opted_out">Opted Out</option>
                        </select>
                        {userRole === 'admin' && <Button
                          variant="outline"
                          size="sm"
                          className="text-xs h-7"
                          onClick={async () => {
                            try {
                              await authFetch(`${API_URL}/api/admin/followups/resend/${fu.id}?test_mode=true`, {
                                method: 'POST'
                              });
                              alert('Test email sent to team');
                            } catch (err) { console.error(err); }
                          }}
                        >
                          Resend (Test)
                        </Button>}
                      </div>
                    )}
                  </div>
                ))}
                {(followupData.followups || []).length === 0 && (
                  <div className="p-8 text-center text-slate-500">
                    No follow-ups yet. They will appear here after students complete assessments.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Settings Tab - Google Sheets Integration (Admin only) */}
        {activeTab === 'settings' && userRole === 'admin' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-emerald-100 rounded-xl">
                  <FileSpreadsheet className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Google Sheets Integration</h2>
                  <p className="text-sm text-slate-500">Sync all leads to Google Sheets for operational management</p>
                </div>
              </div>

              {sheetsMessage && (
                <div className={`mb-4 p-3 rounded-lg text-sm ${
                  sheetsMessage.includes('Error') || sheetsMessage.includes('failed') 
                    ? 'bg-red-50 text-red-700' 
                    : 'bg-emerald-50 text-emerald-700'
                }`}>
                  {sheetsMessage}
                </div>
              )}

              {!sheetsStatus ? (
                <div className="text-center py-8">
                  <Button
                    onClick={async () => {
                      setSheetsLoading(true);
                      setSheetsMessage('');
                      try {
                        const res = await authFetch(`${API_URL}/api/admin/sheets/status`);
                        const data = await res.json();
                        setSheetsStatus(data);
                      } catch (err) {
                        setSheetsMessage('Error: ' + err.message);
                      } finally {
                        setSheetsLoading(false);
                      }
                    }}
                    disabled={sheetsLoading}
                    className="bg-slate-100 text-slate-700 hover:bg-slate-200"
                  >
                    {sheetsLoading ? 'Loading...' : 'Check Integration Status'}
                  </Button>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Status Display */}
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="bg-slate-50 rounded-xl p-4">
                      <p className="text-xs text-slate-500 uppercase font-medium mb-1">Configuration</p>
                      <p className={`font-semibold flex items-center gap-2 ${sheetsStatus.configured ? 'text-emerald-600' : 'text-amber-600'}`}>
                        {sheetsStatus.configured ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
                        {sheetsStatus.configured ? 'Configured' : 'Not Configured'}
                      </p>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-4">
                      <p className="text-xs text-slate-500 uppercase font-medium mb-1">Connection</p>
                      <p className={`font-semibold flex items-center gap-2 ${sheetsStatus.connected ? 'text-emerald-600' : 'text-amber-600'}`}>
                        {sheetsStatus.connected ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
                        {sheetsStatus.connected ? 'Connected' : 'Not Connected'}
                      </p>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-4">
                      <p className="text-xs text-slate-500 uppercase font-medium mb-1">Failed Syncs</p>
                      <p className={`font-semibold ${sheetsStatus.pending_failed_syncs > 0 ? 'text-amber-600' : 'text-slate-700'}`}>
                        {sheetsStatus.pending_failed_syncs || 0} pending
                      </p>
                    </div>
                  </div>

                  {/* Not Configured Message */}
                  {!sheetsStatus.configured && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                      <h3 className="font-semibold text-amber-800 mb-2">Setup Required</h3>
                      <p className="text-sm text-amber-700 mb-3">
                        To enable Google Sheets integration, add the following environment variables to your backend:
                      </p>
                      <ul className="text-sm text-amber-700 list-disc list-inside space-y-1">
                        <li><code className="bg-amber-100 px-1 rounded">GOOGLE_SHEETS_CLIENT_ID</code></li>
                        <li><code className="bg-amber-100 px-1 rounded">GOOGLE_SHEETS_CLIENT_SECRET</code></li>
                        <li><code className="bg-amber-100 px-1 rounded">GOOGLE_SHEETS_REDIRECT_URI</code></li>
                      </ul>
                    </div>
                  )}

                  {/* Connect Button */}
                  {sheetsStatus.configured && !sheetsStatus.connected && (
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                      <h3 className="font-semibold text-blue-800 mb-2">Connect Your Google Account</h3>
                      <p className="text-sm text-blue-700 mb-4">
                        Authorize Elevate Careers to write leads to your Google Sheets.
                      </p>
                      <Button
                        onClick={async () => {
                          setSheetsLoading(true);
                          setSheetsMessage('');
                          try {
                            const res = await authFetch(`${API_URL}/api/admin/sheets/connect`);
                            const data = await res.json();
                            if (data.authorization_url) {
                              window.location.href = data.authorization_url;
                            }
                          } catch (err) {
                            setSheetsMessage('Error: ' + err.message);
                            setSheetsLoading(false);
                          }
                        }}
                        disabled={sheetsLoading}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        <ExternalLink size={16} className="mr-2" />
                        Connect Google Account
                      </Button>
                    </div>
                  )}

                  {/* Configure Spreadsheet */}
                  {sheetsStatus.connected && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                      <h3 className="font-semibold text-emerald-800 mb-2">Configure Spreadsheet</h3>
                      {sheetsStatus.spreadsheet_id ? (
                        <div>
                          <p className="text-sm text-emerald-700 mb-2">
                            Current Spreadsheet ID: <code className="bg-emerald-100 px-1 rounded">{sheetsStatus.spreadsheet_id}</code>
                          </p>
                          <a 
                            href={`https://docs.google.com/spreadsheets/d/${sheetsStatus.spreadsheet_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-emerald-600 hover:underline flex items-center gap-1"
                          >
                            <ExternalLink size={14} />
                            Open in Google Sheets
                          </a>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <p className="text-sm text-emerald-700">
                            Enter the ID of your Google Sheet (the long string in the URL between /d/ and /edit).
                          </p>
                          <div className="flex gap-2">
                            <Input
                              type="text"
                              placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms"
                              value={sheetsSpreadsheetId}
                              onChange={(e) => setSheetsSpreadsheetId(e.target.value)}
                              className="flex-1"
                            />
                            <Button
                              onClick={async () => {
                                if (!sheetsSpreadsheetId.trim()) {
                                  setSheetsMessage('Please enter a Spreadsheet ID');
                                  return;
                                }
                                setSheetsLoading(true);
                                setSheetsMessage('');
                                try {
                                  const res = await authFetch(`${API_URL}/api/admin/sheets/configure`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ spreadsheet_id: sheetsSpreadsheetId })
                                  });
                                  const data = await res.json();
                                  if (data.success) {
                                    setSheetsMessage('Spreadsheet configured and initialized successfully!');
                                    // Refresh status
                                    const statusRes = await authFetch(`${API_URL}/api/admin/sheets/status`);
                                    setSheetsStatus(await statusRes.json());
                                  } else {
                                    setSheetsMessage('Error: ' + (data.detail || data.error || 'Configuration failed'));
                                  }
                                } catch (err) {
                                  setSheetsMessage('Error: ' + err.message);
                                } finally {
                                  setSheetsLoading(false);
                                }
                              }}
                              disabled={sheetsLoading}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            >
                              {sheetsLoading ? 'Configuring...' : 'Initialize Spreadsheet'}
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Retry Failed Syncs */}
                  {sheetsStatus.pending_failed_syncs > 0 && (
                    <div className="flex justify-end">
                      <Button
                        onClick={async () => {
                          setSheetsLoading(true);
                          setSheetsMessage('');
                          try {
                            const res = await authFetch(`${API_URL}/api/admin/sheets/retry-failed`, {
                              method: 'POST'
                            });
                            const data = await res.json();
                            setSheetsMessage(`Retried ${data.retried} failed syncs. ${data.succeeded} succeeded.`);
                            // Refresh status
                            const statusRes = await authFetch(`${API_URL}/api/admin/sheets/status`);
                            setSheetsStatus(await statusRes.json());
                          } catch (err) {
                            setSheetsMessage('Error: ' + err.message);
                          } finally {
                            setSheetsLoading(false);
                          }
                        }}
                        disabled={sheetsLoading}
                        variant="outline"
                        className="text-amber-600 border-amber-300 hover:bg-amber-50"
                      >
                        <RefreshCw size={16} className="mr-2" />
                        Retry Failed Syncs ({sheetsStatus.pending_failed_syncs})
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Danger Zone - Clear All Data */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-red-100 mt-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-red-100 rounded-xl">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Danger Zone</h2>
                  <p className="text-sm text-slate-500">Irreversible actions</p>
                </div>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <h3 className="font-semibold text-red-800 mb-2">Clear All Data</h3>
                <p className="text-sm text-red-700 mb-4">
                  This will permanently delete all leads, applications, inquiries and submissions from the database. This action cannot be undone.
                </p>
                <Button
                  onClick={async () => {
                    if (!window.confirm('Are you sure you want to delete ALL data? This cannot be undone!')) {
                      return;
                    }
                    if (!window.confirm('FINAL WARNING: This will delete all leads, applications, and submissions. Type "DELETE" in the next prompt to confirm.')) {
                      return;
                    }
                    const confirmText = window.prompt('Type DELETE to confirm:');
                    if (confirmText !== 'DELETE') {
                      alert('Deletion cancelled.');
                      return;
                    }
                    
                    try {
                      const res = await authFetch(`${API_URL}/api/admin/clear-all-data`, {
                        method: 'POST'
                      });
                      const data = await res.json();
                      if (data.success) {
                        alert(`Successfully cleared ${data.message}`);
                        window.location.reload();
                      } else {
                        alert('Error: ' + (data.detail || 'Failed to clear data'));
                      }
                    } catch (err) {
                      alert('Error: ' + err.message);
                    }
                  }}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  Clear All Data
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Admin;
